/**
 * The collapsed `Inferência <model> Editar` line under an LLM operation's
 * functional row, and its expanded plain-JSON editor (`659:19825`).
 *
 * A separate resource from the operation's own pricing config: `GET/PUT
 * /ops/inference/{task}` (`task_inference_configs`), keyed by `TaskType`
 * alone, not by network or provider suffix -- `props.task` is that bare key
 * (`chat`, `post_intelligence`, ...). The operation's saved provider and key are
 * what the task runs on -- saving the operation points the task at them -- so
 * a config save sends those, never the row's own, and no secondary.
 */
import { useState, type ReactNode } from 'react';

import { failureSentence } from '../../chrome/state';
import { ApiFailureError, type ApiFailure } from '../../lib/http/errors';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { useInferenceTasks, useSaveInferenceConfig } from '../api/queries';
import type { InferenceTask } from '../api/schemas';
import { operationNameOf, t } from './messages';

/** The persisted row's `config` is a JSON object on the wire
 *  (`InferenceTaskOut.config`); the textarea below edits it as text, the
 *  shape a save actually sends (`InferenceChoiceIn.config: str`). */
function configText(config: InferenceTask['config']): string {
  return JSON.stringify(config);
}

function modelOf(config: InferenceTask['config']): string | null {
  const model = config.model;
  return typeof model === 'string' ? model : null;
}

export function InferenceRow(props: {
  task: string;
  operationKey: string;
  provider: string;
  keyId: string | null;
}): ReactNode {
  const query = useInferenceTasks();
  const save = useSaveInferenceConfig();
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const found = query.data?.find((candidate) => candidate.task === props.task);
  if (found === undefined) return null;
  // A plain `const` alias, not `found` itself: a nested function declaration
  // is not in the narrowing's control-flow graph, so TypeScript would
  // otherwise widen `found` back to its optional type at every use below.
  const row = found;

  const text = draft ?? configText(row.config);
  const model = modelOf(row.config);
  const operationName = operationNameOf(props.operationKey);

  function persist(change: {
    config?: string;
    priority?: InferenceTask['priority'];
    flexFallbackAfter?: number | null;
  }): void {
    setFailure(null);
    save.mutate(
      {
        task: row.task,
        provider: props.provider,
        config: change.config ?? configText(row.config),
        providerKeyId: props.keyId,
        secondaryProvider: null,
        secondaryModelId: null,
        secondaryProviderKeyId: null,
        priority: change.priority ?? row.priority,
        flexFallbackAfter:
          change.flexFallbackAfter === undefined
            ? row.flex_fallback_after
            : change.flexFallbackAfter,
      },
      {
        onSuccess: () => setDraft(null),
        onError: (error) => {
          setFailure(
            error instanceof ApiFailureError ? error.failure : { kind: 'server', status: 0 },
          );
        },
      },
    );
  }

  function handleBlur(): void {
    if (draft === null || draft === configText(row.config)) return; // unchanged -- nothing to save
    persist({ config: draft });
  }

  function handleFallbackBlur(value: string): void {
    const next = value.trim() === '' ? null : Number(value);
    if (next === row.flex_fallback_after) return;
    persist({ flexFallbackAfter: next });
  }

  return (
    <div className="flex flex-col gap-2 border-t px-5 py-2.5">
      <div className="flex items-center gap-2 text-body-sm">
        <span className="text-muted-foreground">{t('inference.label')}</span>
        <span>{model ?? t('inference.noModel')}</span>
        <div className="flex-1" />
        <Select
          value={row.priority}
          // The assistant chat answers a person waiting on it; the API refuses it low.
          disabled={row.task === 'chat' || save.isPending}
          onValueChange={(value) => persist({ priority: value as InferenceTask['priority'] })}
        >
          <SelectTrigger
            aria-label={t('inference.aria.priority', { operation: operationName })}
            size="sm"
          >
            <SelectValue>{t(`inference.priority.${row.priority}` as const)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">{t('inference.priority.default')}</SelectItem>
            <SelectItem value="low">{t('inference.priority.low')}</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}>
          {expanded ? t('inference.collapse') : t('inference.edit')}
        </Button>
      </div>
      {expanded && (
        <>
          <Textarea
            aria-label={t('inference.aria.textarea', { operation: operationName })}
            aria-invalid={failure !== null}
            className="font-mono text-xs"
            value={text}
            onChange={(event) => {
              setFailure(null);
              setDraft(event.target.value);
            }}
            onBlur={handleBlur}
          />
          {row.priority === 'low' && (
            <label className="flex items-center gap-2 text-caption text-muted-foreground">
              {t('inference.fallback')}
              <Input
                key={row.flex_fallback_after ?? 'off'}
                type="number"
                min={1}
                step={1}
                className="w-20"
                aria-label={t('inference.aria.fallback', { operation: operationName })}
                defaultValue={row.flex_fallback_after ?? ''}
                onBlur={(event) => handleFallbackBlur(event.target.value)}
              />
            </label>
          )}
          {failure !== null && (
            <p role="alert" className="text-caption text-destructive">
              {failure.kind === 'invalid' ? failure.message : failureSentence(failure)}
            </p>
          )}
        </>
      )}
    </div>
  );
}
